import { useState, useCallback } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import JSZip from "jszip";

/* ── Export action config ─────────────────────── */

export const DOWNLOAD_ACTIONS = [
  { id: "download_zip",       icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4", fileFormat: "zip" },
  { id: "download_pdf_merge", icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", fileFormat: "pdf" },
];

export const EXPORT_ACTIONS = [
  { id: "csv",              icon: "M4 4v16h16V8l-4-4H4zm10 0v4h4M8 12h8M8 16h5", selectionMode: "both", outputType: "file", fileFormat: "csv"  },
  { id: "excel",            icon: "M4 4v16h16V8l-4-4H4zm10 0v4h4M8 12h8M8 16h5", selectionMode: "both", outputType: "file", fileFormat: "xlsx" },
  { id: "json",             icon: "M4 4v16h16V8l-4-4H4zm10 0v4h4M8 12h8M8 16h5", selectionMode: "both", outputType: "file", fileFormat: "json" },
  { id: "extracted_fields", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2", selectionMode: "both", outputType: "file", fileFormat: "xlsx" },
  { id: "summary_report",   icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", selectionMode: "both", outputType: "file", fileFormat: "pdf" },
  { id: "audit_package",    icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4", selectionMode: "both", outputType: "file", fileFormat: "zip" },
];

/* ── Field helpers (case-insensitive) ─────────── */

function ci(obj, keys) {
  if (!obj) return "";
  const map = new Map(Object.keys(obj).map(k => [k.toLowerCase(), k]));
  for (const k of keys) {
    const real = map.get(k.toLowerCase());
    if (real && obj[real] != null && obj[real] !== "") return obj[real];
  }
  return "";
}

function docId(doc)        { return ci(doc, ["id", "Id", "itemId", "ItemId", "archiveNo", "ArchiveNo"]); }
function fileName(doc)     { return ci(doc, ["originalName", "fileName", "documentName", "name"]) || "Untitled"; }
function docType(doc)      { return ci(doc, ["documentType", "docType", "type", "fileExtension", "extension"]); }
function customerNo(doc)   { return ci(doc, ["customerNo", "customerNumber", "customer"]); }
function archiveNo(doc)    { return ci(doc, ["archiveNo", "ArchiveNo", "archive"]); }
function accountNo(doc)    { return ci(doc, ["accountNo", "accountNumber", "account"]); }
function docDate(doc)      { return ci(doc, ["documentDate", "date", "createdAt", "created", "uploadDate"]); }
function pageCount(doc)    { return ci(doc, ["pageCount", "pages"]); }
function docStatus(doc)    { return ci(doc, ["status", "Status"]); }
function extractedAmt(doc) { return ci(doc, ["amount", "total", "invoiceAmount", "balance"]); }
function extractedRef(doc) { return ci(doc, ["invoiceNumber", "invoiceNo", "invoice", "referenceNo", "reference"]); }
function dbName(doc)       { return ci(doc, ["dbName", "DbName", "database", "Database", "db"]); }
function docLink(doc)      { return ci(doc, ["itemId", "ItemId", "link", "Link"]); }
function fileExt(doc)      { return (ci(doc, ["fileExtension", "extension"]) || "").toString().toLowerCase().replace(/^\./, ""); }

function docTags(doc) {
  const raw = ci(doc, ["tags", "Tags"]);
  if (Array.isArray(raw)) return raw.join(", ");
  return typeof raw === "string" ? raw : "";
}

/* ── Build flat row for CSV / Excel ───────────── */

function buildMetadataRow(doc) {
  return {
    document_id:         String(docId(doc)),
    file_name:           String(fileName(doc)),
    document_type:       String(docType(doc)),
    customer_no:         String(customerNo(doc)),
    archive_no:          String(archiveNo(doc)),
    account_no:          String(accountNo(doc)),
    date:                String(docDate(doc)),
    page_count:          String(pageCount(doc)),
    status:              String(docStatus(doc)),
    extracted_amount:    String(extractedAmt(doc)),
    extracted_reference: String(extractedRef(doc)),
    tags:                String(docTags(doc)),
  };
}

/* ── Build extracted-fields row ───────────────── */

function buildExtractedRow(doc) {
  return {
    document_id:    String(docId(doc)),
    file_name:      String(fileName(doc)),
    account_no:     String(accountNo(doc)),
    customer_no:    String(customerNo(doc)),
    document_type:  String(docType(doc)),
    date:           String(docDate(doc)),
    invoice_no:     String(extractedRef(doc)),
    amount:         String(extractedAmt(doc)),
    currency:       String(ci(doc, ["currency", "Currency"])),
    database:       String(dbName(doc)),
  };
}

/* ── Build JSON payload ───────────────────────── */

function buildJsonPayload(docs) {
  return {
    exported_at: new Date().toISOString(),
    document_count: docs.length,
    documents: docs.map(doc => ({
      id: docId(doc),
      fileName: fileName(doc),
      metadata: {
        documentType: docType(doc),
        customerNo: customerNo(doc),
        archiveNo: archiveNo(doc),
        accountNo: accountNo(doc),
        date: docDate(doc),
        pageCount: pageCount(doc),
        status: docStatus(doc),
        database: dbName(doc),
      },
      extractedFields: {
        amount: extractedAmt(doc),
        reference: extractedRef(doc),
        currency: ci(doc, ["currency", "Currency"]),
      },
      tags: docTags(doc) ? docTags(doc).split(", ") : [],
    })),
  };
}

/* ── File generators (CSV / Excel / JSON / Extracted) ── */

function generateCSV(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = v => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  for (const row of rows) {
    lines.push(headers.map(h => escape(row[h])).join(","));
  }
  return lines.join("\r\n");
}

function downloadCSV(docs) {
  const rows = docs.map(buildMetadataRow);
  const csv = generateCSV(rows);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  saveAs(blob, `export_${ts()}.csv`);
}

function downloadExcel(docs) {
  const rows = docs.map(buildMetadataRow);
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Documents");
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, `export_${ts()}.xlsx`);
}

function downloadJSON(docs) {
  const payload = buildJsonPayload(docs);
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });
  saveAs(blob, `export_${ts()}.json`);
}

function downloadExtractedFields(docs) {
  const rows = docs.map(buildExtractedRow);
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Extracted Fields");
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, `extracted_fields_${ts()}.xlsx`);
}

function ts() {
  return new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
}

/* ── AI summary fetcher ──────────────────────── */

function buildDocMetadata(doc, fallbackDb) {
  const meta = {};
  const n = fileName(doc);        if (n) meta.fileName = n;
  const e = fileExt(doc);          if (e) meta.fileExtension = e;
  const d = dbName(doc) || fallbackDb; if (d) meta.database = d;
  const a = archiveNo(doc);        if (a) meta.archiveNo = a;
  const dt = docDate(doc);         if (dt) meta.documentDate = typeof dt === "string" ? dt : new Date(dt).toISOString();
  return meta;
}

async function fetchAiReport(docs, token, apiBase, fallbackDb) {
  const documents = docs.map(doc => ({
    link: docLink(doc) || "",
    metadata: buildDocMetadata(doc, fallbackDb),
  }));

  const resp = await fetch(`${apiBase}/api/AiAssist/documents`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action: "report", customPrompt: null, documents }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.message || `AI report failed (HTTP ${resp.status})`);
  }
  const data = await resp.json();
  return data.result || "";
}

/* ── Document file fetcher ───────────────────── */

async function fetchDocumentBlob(doc, token, apiBase) {
  const link = docLink(doc);
  if (!link) return null;

  const resp = await fetch(`${apiBase}/api/Document/ViewDocument`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ requests: [{ requestType: 0, link }] }),
  });
  if (!resp.ok) return null;
  const buf = await resp.arrayBuffer();
  return new Uint8Array(buf);
}

/* ── PDF Summary Report ──────────────────────── */

// jsPDF color constants
const CLR_PRIMARY   = [21, 53, 117];   // dark navy
const CLR_ACCENT    = [13, 148, 136];   // teal
const CLR_GRAY      = [107, 114, 128];
const CLR_LIGHT_BG  = [245, 247, 250];
const CLR_WHITE     = [255, 255, 255];
const CLR_BLACK     = [26, 26, 46];
const CLR_BORDER    = [226, 229, 239];

function addPageFooter(pdf, pageNum, totalLabel) {
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  pdf.setDrawColor(...CLR_BORDER);
  pdf.line(20, ph - 16, pw - 20, ph - 16);
  pdf.setFontSize(8);
  pdf.setTextColor(...CLR_GRAY);
  pdf.text(totalLabel, 20, ph - 10);
  pdf.text(`Page ${pageNum}`, pw - 20, ph - 10, { align: "right" });
}

function pdfMetaTable(pdf, doc, startY, pw) {
  const fields = [
    ["Document ID", String(docId(doc))],
    ["File Name",   String(fileName(doc))],
    ["Type",        String(docType(doc))],
    ["Customer",    String(customerNo(doc))],
    ["Archive No",  String(archiveNo(doc))],
    ["Account",     String(accountNo(doc))],
    ["Date",        String(docDate(doc))],
    ["Pages",       String(pageCount(doc))],
    ["Status",      String(docStatus(doc))],
    ["Amount",      String(extractedAmt(doc))],
    ["Reference",   String(extractedRef(doc))],
    ["Tags",        String(docTags(doc))],
    ["Database",    String(dbName(doc))],
  ].filter(([, v]) => v);

  let y = startY;
  const rowH = 16;          // 9pt font needs ~16pt row height
  const labelX = 24;
  const valueX = 140;       // fixed column for values

  for (let i = 0; i < fields.length; i++) {
    const [label, value] = fields[i];
    if (i % 2 === 0) {
      pdf.setFillColor(...CLR_LIGHT_BG);
      pdf.rect(20, y, pw - 40, rowH, "F");
    }
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...CLR_GRAY);
    pdf.text(label, labelX, y + 11);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...CLR_BLACK);
    const maxValW = pw - valueX - 24;
    const trimmed = value.length > 80 ? value.slice(0, 77) + "..." : value;
    const valLines = pdf.splitTextToSize(trimmed, maxValW);
    pdf.text(valLines[0], valueX, y + 11);
    y += rowH;
  }
  return y;
}

function pdfCheckPage(pdf, y, needed) {
  if (y + needed > pdf.internal.pageSize.getHeight() - 36) {
    pdf.addPage();
    return 30;
  }
  return y;
}

function pdfRenderMarkdown(pdf, markdown, startY, pw) {
  let y = startY;
  const maxW = pw - 48;
  const bodyLH = 13;        // line-height for 9pt body text
  const lines = markdown.split("\n");

  for (const line of lines) {
    y = pdfCheckPage(pdf, y, 20);

    const trimmed = line.trim();
    if (!trimmed) { y += 6; continue; }

    // Headings
    const hMatch = trimmed.match(/^(#{1,4})\s+(.*)/);
    if (hMatch) {
      const level = hMatch[1].length;
      const sizes = [14, 13, 11, 10];
      const lh    = [18, 17, 15, 14];
      const sz = sizes[level - 1] || 10;
      const lineH = lh[level - 1] || 14;
      y += level === 1 ? 10 : 6; // spacing before heading
      y = pdfCheckPage(pdf, y, lineH + 4);
      pdf.setFontSize(sz);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...CLR_PRIMARY);
      const text = hMatch[2].replace(/\*\*/g, "");
      const wrapped = pdf.splitTextToSize(text, maxW);
      pdf.text(wrapped, 24, y);
      y += wrapped.length * lineH + 4;
      continue;
    }

    // List items
    if (/^\s*[-*]\s+/.test(trimmed)) {
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...CLR_BLACK);
      const text = trimmed.replace(/^\s*[-*]\s+/, "").replace(/\*\*/g, "");
      const wrapped = pdf.splitTextToSize(text, maxW - 16);
      y = pdfCheckPage(pdf, y, wrapped.length * bodyLH);
      pdf.text("\u2022", 28, y);
      pdf.text(wrapped, 38, y);
      y += wrapped.length * bodyLH + 2;
      continue;
    }

    // Numbered list
    const numMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
    if (numMatch) {
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...CLR_BLACK);
      const text = numMatch[2].replace(/\*\*/g, "");
      const wrapped = pdf.splitTextToSize(text, maxW - 18);
      y = pdfCheckPage(pdf, y, wrapped.length * bodyLH);
      pdf.text(`${numMatch[1]}.`, 24, y);
      pdf.text(wrapped, 38, y);
      y += wrapped.length * bodyLH + 2;
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      y += 4;
      pdf.setDrawColor(...CLR_BORDER);
      pdf.line(24, y, pw - 24, y);
      y += 8;
      continue;
    }

    // Regular paragraph
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...CLR_BLACK);
    const text = trimmed.replace(/\*\*/g, "");
    const wrapped = pdf.splitTextToSize(text, maxW);
    y = pdfCheckPage(pdf, y, wrapped.length * bodyLH);
    pdf.text(wrapped, 24, y);
    y += wrapped.length * bodyLH + 3;
  }

  return y;
}

async function downloadSummaryReport(docs, { token, apiBase, fallbackDb, onProgress }) {
  if (onProgress) onProgress("ai");
  let aiMarkdown = "";
  try {
    aiMarkdown = await fetchAiReport(docs, token, apiBase, fallbackDb);
  } catch {
    // Continue without AI summary if it fails
  }

  if (onProgress) onProgress("pdf");

  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pw = pdf.internal.pageSize.getWidth();
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const footerLabel = `Depository Export \u2014 ${dateStr}`;

  // ── Cover / Title ──
  pdf.setFillColor(...CLR_PRIMARY);
  pdf.rect(0, 0, pw, 90, "F");
  pdf.setTextColor(...CLR_WHITE);
  pdf.setFontSize(22);
  pdf.setFont("helvetica", "bold");
  pdf.text("Document Summary Report", 24, 42);
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  pdf.text(`${docs.length} document${docs.length !== 1 ? "s" : ""} \u2022 Generated ${dateStr}`, 24, 62);

  let y = 110;
  let pageNum = 1;

  // ── Document sections ──
  for (let i = 0; i < docs.length; i++) {
    const doc = docs[i];

    // Page break if not enough space
    if (y > pdf.internal.pageSize.getHeight() - 120) {
      addPageFooter(pdf, pageNum, footerLabel);
      pageNum++;
      pdf.addPage();
      y = 25;
    }

    // Section header
    pdf.setFillColor(...CLR_ACCENT);
    pdf.rect(20, y, pw - 40, 22, "F");
    pdf.setTextColor(...CLR_WHITE);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text(`${i + 1}. ${fileName(doc)}`, 28, y + 15);
    y += 30;

    // Metadata table
    y = pdfMetaTable(pdf, doc, y, pw);
    y += 8;
  }

  // ── AI Report section ──
  if (aiMarkdown) {
    if (y > pdf.internal.pageSize.getHeight() - 100) {
      addPageFooter(pdf, pageNum, footerLabel);
      pageNum++;
      pdf.addPage();
      y = 25;
    }

    pdf.setFillColor(...CLR_PRIMARY);
    pdf.rect(20, y, pw - 40, 22, "F");
    pdf.setTextColor(...CLR_WHITE);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("AI Analysis", 28, y + 15);
    y += 32;

    y = pdfRenderMarkdown(pdf, aiMarkdown, y, pw);
  }

  // Footer on last page
  addPageFooter(pdf, pageNum, footerLabel);

  // Add footers to all intermediate pages
  const totalPages = pdf.internal.getNumberOfPages();
  for (let p = 1; p < totalPages; p++) {
    pdf.setPage(p);
    addPageFooter(pdf, p, footerLabel);
  }

  const blob = pdf.output("blob");
  saveAs(blob, `summary_report_${ts()}.pdf`);
}

/* ── Audit Package (ZIP) ─────────────────────── */

async function downloadAuditPackage(docs, { token, apiBase, fallbackDb, onProgress }) {
  const zip = new JSZip();
  const now = new Date();
  const dateStr = now.toISOString();

  // 1. manifest.txt
  const manifestLines = [
    "DEPOSITORY AUDIT PACKAGE",
    "========================",
    "",
    `Generated: ${dateStr}`,
    `Documents: ${docs.length}`,
    "",
    "Contents:",
    "  manifest.txt         — This file",
    "  metadata.json        — Full document metadata (machine-readable)",
    "  metadata.csv         — Document metadata (spreadsheet-friendly)",
    "  summary_report.pdf   — AI-generated summary report",
    "  documents/           — Original document files",
    "",
    "Document List:",
  ];
  docs.forEach((doc, i) => {
    manifestLines.push(`  ${i + 1}. ${fileName(doc)} (ID: ${docId(doc)})`);
  });
  zip.file("manifest.txt", manifestLines.join("\n"));

  // 2. metadata.json
  const jsonPayload = buildJsonPayload(docs);
  zip.file("metadata.json", JSON.stringify(jsonPayload, null, 2));

  // 3. metadata.csv
  const csvRows = docs.map(buildMetadataRow);
  const csvContent = generateCSV(csvRows);
  zip.file("metadata.csv", "\uFEFF" + csvContent);

  // 4. Original documents
  if (onProgress) onProgress("documents");
  const docFolder = zip.folder("documents");
  const fetchResults = await Promise.allSettled(
    docs.map(doc => fetchDocumentBlob(doc, token, apiBase))
  );
  fetchResults.forEach((result, i) => {
    if (result.status === "fulfilled" && result.value) {
      const doc = docs[i];
      const ext = fileExt(doc) || "bin";
      const name = fileName(doc).replace(/[/\\:*?"<>|]/g, "_");
      const safeName = `${String(i + 1).padStart(3, "0")}_${name}${name.toLowerCase().endsWith(`.${ext}`) ? "" : `.${ext}`}`;
      docFolder.file(safeName, result.value);
    }
  });

  // 5. Summary report PDF
  if (onProgress) onProgress("report");
  try {
    // Build the PDF in-memory and add to ZIP
    let aiMarkdown = "";
    try {
      aiMarkdown = await fetchAiReport(docs, token, apiBase, fallbackDb);
    } catch {
      // Continue without AI report
    }

    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    const footerDateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
    const footerLabel = `Depository Audit Export \u2014 ${footerDateStr}`;

    pdf.setFillColor(...CLR_PRIMARY);
    pdf.rect(0, 0, pw, 90, "F");
    pdf.setTextColor(...CLR_WHITE);
    pdf.setFontSize(22);
    pdf.setFont("helvetica", "bold");
    pdf.text("Audit Summary Report", 24, 42);
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "normal");
    pdf.text(`${docs.length} document${docs.length !== 1 ? "s" : ""} \u2022 Generated ${footerDateStr}`, 24, 62);

    let y = 110;
    for (let i = 0; i < docs.length; i++) {
      const doc = docs[i];
      if (y > pdf.internal.pageSize.getHeight() - 120) {
        pdf.addPage();
        y = 25;
      }
      pdf.setFillColor(...CLR_ACCENT);
      pdf.rect(20, y, pw - 40, 22, "F");
      pdf.setTextColor(...CLR_WHITE);
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text(`${i + 1}. ${fileName(doc)}`, 28, y + 15);
      y += 30;
      y = pdfMetaTable(pdf, doc, y, pw);
      y += 8;
    }

    if (aiMarkdown) {
      if (y > pdf.internal.pageSize.getHeight() - 100) {
        pdf.addPage();
        y = 25;
      }
      pdf.setFillColor(...CLR_PRIMARY);
      pdf.rect(20, y, pw - 40, 22, "F");
      pdf.setTextColor(...CLR_WHITE);
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text("AI Analysis", 28, y + 15);
      y += 32;
      pdfRenderMarkdown(pdf, aiMarkdown, y, pw);
    }

    const totalPages = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      addPageFooter(pdf, p, footerLabel);
    }

    const pdfArrayBuf = pdf.output("arraybuffer");
    zip.file("summary_report.pdf", new Uint8Array(pdfArrayBuf));
  } catch {
    // If PDF generation fails, include a note
    zip.file("summary_report_error.txt", "PDF report generation failed. Metadata files are still included.");
  }

  // Generate ZIP
  if (onProgress) onProgress("zip");
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  saveAs(blob, `audit_package_${ts()}.zip`);
}

/* ── Download as ZIP (files only) ────────────── */

async function downloadFilesAsZip(docs, { token, apiBase, onProgress }) {
  if (onProgress) onProgress("fetch_files");
  const results = await Promise.allSettled(
    docs.map(doc => fetchDocumentBlob(doc, token, apiBase))
  );
  const zip = new JSZip();
  results.forEach((result, i) => {
    if (result.status === "fulfilled" && result.value) {
      const doc = docs[i];
      const ext = fileExt(doc) || "bin";
      const name = fileName(doc).replace(/[/\\:*?"<>|]/g, "_");
      const safeName = `${String(i + 1).padStart(3, "0")}_${name}${name.toLowerCase().endsWith(`.${ext}`) ? "" : `.${ext}`}`;
      zip.file(safeName, result.value);
    }
  });
  if (onProgress) onProgress("package_zip");
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  saveAs(blob, `files_${ts()}.zip`);
}

/* ── Download as Merged PDF ───────────────────── */

async function downloadMergedPdf(docs, { token, apiBase, onProgress }) {
  if (onProgress) onProgress("fetch_files");
  const results = await Promise.allSettled(
    docs.map(doc => fetchDocumentBlob(doc, token, apiBase))
  );

  if (onProgress) onProgress("render_pages");
  const pdfjsLib = await import("pdfjs-dist/build/pdf");
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

  const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const PAGE_W = 595.28;
  const PAGE_H = 841.89;
  let firstPage = true;

  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (result.status !== "fulfilled" || !result.value) continue;
    try {
      const pdfDoc = await pdfjsLib.getDocument({ data: result.value }).promise;
      for (let p = 1; p <= pdfDoc.numPages; p++) {
        const page = await pdfDoc.getPage(p);
        const vp = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width;
        canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
        const imgData = canvas.toDataURL("image/jpeg", 0.85);
        const aspect = vp.width / vp.height;
        let dw, dh, dx, dy;
        if (aspect > PAGE_W / PAGE_H) {
          dw = PAGE_W; dh = PAGE_W / aspect; dx = 0; dy = (PAGE_H - dh) / 2;
        } else {
          dh = PAGE_H; dw = PAGE_H * aspect; dx = (PAGE_W - dw) / 2; dy = 0;
        }
        if (!firstPage) pdf.addPage();
        firstPage = false;
        pdf.addImage(imgData, "JPEG", dx, dy, dw, dh);
      }
    } catch {
      // Skip files that can't be parsed as PDF
    }
  }

  if (firstPage) throw new Error("No PDF pages could be rendered");
  if (onProgress) onProgress("merge_pdf");
  saveAs(pdf.output("blob"), `merged_${ts()}.pdf`);
}

/* ── Action dispatcher ────────────────────────── */

// Simple handlers (no network needed)
const SYNC_HANDLERS = {
  csv:              downloadCSV,
  excel:            downloadExcel,
  json:             downloadJSON,
  extracted_fields: downloadExtractedFields,
};

// Async handlers (need token, apiBase)
const ASYNC_HANDLERS = {
  summary_report:     downloadSummaryReport,
  audit_package:      downloadAuditPackage,
  download_zip:       downloadFilesAsZip,
  download_pdf_merge: downloadMergedPdf,
};

/* ── Hook ─────────────────────────────────────── */

export default function useBatchExport({ token, apiBase, fallbackDb } = {}) {
  const [exportLoading, setExportLoading] = useState(null);  // action id or null
  const [exportError, setExportError]     = useState("");
  const [exportPhase, setExportPhase]     = useState("");     // "ai" | "pdf" | "documents" | "report" | "zip" | ""

  const runExport = useCallback(async (actionId, docs) => {
    if (exportLoading) return; // prevent double-fire
    setExportError("");
    setExportPhase("");

    // Check sync handlers first
    const syncHandler = SYNC_HANDLERS[actionId];
    if (syncHandler) {
      setExportLoading(actionId);
      try {
        await new Promise(r => setTimeout(r, 0));
        syncHandler(docs);
      } catch (err) {
        console.error("Export failed:", err);
        setExportError(err.message || "unknown");
      } finally {
        setExportLoading(null);
        setExportPhase("");
      }
      return;
    }

    // Async handlers
    const asyncHandler = ASYNC_HANDLERS[actionId];
    if (!asyncHandler) {
      setExportError("not_implemented");
      return;
    }

    setExportLoading(actionId);
    try {
      await asyncHandler(docs, {
        token,
        apiBase,
        fallbackDb,
        onProgress: (phase) => setExportPhase(phase),
      });
    } catch (err) {
      console.error("Export failed:", err);
      setExportError(err.message || "unknown");
    } finally {
      setExportLoading(null);
      setExportPhase("");
    }
  }, [exportLoading, token, apiBase, fallbackDb]);

  const clearExportError = useCallback(() => setExportError(""), []);

  return { exportLoading, exportError, exportPhase, runExport, clearExportError };
}
